'use client';

import React from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'framer-motion';
import { containerStagger, fadeUp } from '@/lib/animations';

/**
 * TechnologySection
 *
 * Renders a section highlighting Andoria AI's adaptive, scalable technology solutions.
 * - Left: Large image representing AI technology workspace
 * - Right: Text content describing business challenges and Andoria AI's solutions
 *   - Includes two paragraphs on digital platform hurdles and cloud adoption in Nigeria
 *   - Small image illustrating cloud technology solutions
 * - Uses Framer Motion for entrance animations and reduced motion accessibility
 *
 * Responsive layout adapts for mobile and desktop screens.
 */
const TechnologySection: React.FC = () => {
  // Detects if user prefers reduced motion for accessibility
  const reduce = useReducedMotion();

  return (
    <section className="py-20 px-6 mx-auto">
      {/* Main grid: Large image (left) | Text + small image (right) */}
      <motion.div
        className="flex flex-col lg:flex-row gap-8"
        initial={reduce ? undefined : 'hidden'}
        whileInView={reduce ? undefined : 'visible'}
        viewport={{ once: true, amount: 0.18 }}
        variants={containerStagger}
      >
        {/* Left: Large technology workspace image */}
        <motion.div
          className="relative flex-shrink-0 w-full lg:w-[330px] h-[220px] sm:h-[350px] lg:h-[511px] rounded-[16px] overflow-hidden"
          variants={fadeUp}
        >
          <Image
            src="/images/about/andoria-ai-data-tech.jpg"
            alt="AI Technology workspace"
            fill
            sizes="(max-width: 1023px) 100vw, 330px"
            className="object-cover"
          />
        </motion.div>

        {/* Right: Text content and small image */}
        <motion.div className="flex-1 flex flex-col" variants={fadeUp}>
          {/* Title and introduction */}
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold font-manrope tracking-[-0.96px] lg:leading-[56px] gradient-text bg-clip-text text-transparent mb-4 lg:mb-6 max-w-full lg:max-w-[604px]">
              Technology that adapts, scales, and solves
            </h2>
            <p className="text-base sm:text-lg font-medium text-text-primary leading-6 sm:leading-7 tracking-[-0.36px] font-manrope max-w-full lg:max-w-[1037px]">
              {/* Explains business challenges and Andoria AI's approach */}
              Most organizations are overwhelmed with data but struggle to gain meaningful insights.
              Outdated systems, fragmented teams, and misleading AI promises result in wasted
              potential, delayed decisions, and generic tools that don’t address specific needs.
              Andoria AI solves this by combining human expertise with AI and cloud technologies. We
              integrate legacy systems with intelligent analytics, provide real-time dashboards, and
              build custom AI models tailored to each organization’s unique goals.
            </p>
          </div>

          {/* Flex row: two text blocks and small image */}
          <motion.div className="flex flex-col lg:flex-row gap-6" variants={containerStagger}>
            {/* Left: Two paragraphs on digital platform and cloud challenges */}
            <motion.div className="flex-1 space-y-6" variants={fadeUp}>
              <p className="text-base sm:text-lg font-medium text-text-primary leading-6 sm:leading-7 tracking-[-0.36px] font-manrope">
                {/* Digital platform hurdles and Andoria AI's solutions */}
                Digital platforms face major hurdles like failed payments, fraud, and poor delivery.
                These issues lead to cart abandonment, mistrust, and high operating costs. Andoria
                AI uses adaptive algorithms, NLP mapping, and AR try‑ons. Our NDPA‑compliant cloud
                helps solve Nigeria’s marketplace challenges at scale.
              </p>
              <p className="text-base sm:text-lg font-medium text-text-primary leading-6 sm:leading-7 tracking-[-0.36px] font-manrope">
                {/* Cloud adoption challenges and Andoria AI's approach */}
                Nigeria’s cloud adoption is hindered by cost, power issues, and audits. Andoria AI
                slashes costs using automation and localized edge caching. Our solution ensures
                audit‑readiness while enabling secure, scalable growth.
              </p>
            </motion.div>

            {/* Right: Small image illustrating cloud technology */}
            <motion.div
              className="relative flex-shrink-0 w-full lg:w-[330px] h-[150px] sm:h-[180px] lg:h-[221px] rounded-[16px] overflow-hidden self-start"
              variants={fadeUp}
            >
              <Image
                src="/images/about/andoria-ai-data.jpg"
                alt="Cloud technology solutions"
                fill
                sizes="(max-width: 1023px) 100vw, 330px"
                className="object-cover"
              />
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default TechnologySection;
